import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import Text, { BodySecondary } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import useWindowSize from '@/hooks/useWindowSize';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderCountryAllowedToReceiveSMS } from '@/redux/modules/account/user/user.selectors';
import { openSMSConfirmationModal } from '@/redux/modules/modal';
import { getPermission, requestBrowserPermission } from '@/redux/modules/notification';
import {
    getAllSMSAlertsSubscriptions,
    getSubscribedToSMSAlerts,
    getSubscriptionsById,
    toggleSmsAlert,
} from '@/redux/modules/smsAlerts';
import {
    TrackSmsNotificationToggleEventTrigger,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';
import noop from '@/utils/noop';
import { enableBrowserNotificationsUrl } from '@/utils/urls';

import TableRow from './TableRow';

type Props = {
    alertTypeId: AlertTypes;
    className?: string;
};

const AlertsRow = ({ alertTypeId, className }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    let subscriptions = useAppSelector((state) => getSubscriptionsById(state, alertTypeId));
    if (!Array.isArray(subscriptions)) {
        subscriptions = [];
    }
    const smsAllowed = useAppSelector(getBidderCountryAllowedToReceiveSMS);
    const hasSMSalerts = useAppSelector(getSubscribedToSMSAlerts);
    const allSmsAlerts = useAppSelector(getAllSMSAlertsSubscriptions);

    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();

    const smsBoxCheck = async () => {
        if (hasSMSalerts) {
            await dispatch(toggleSmsAlert(alertTypeId, DeliveryType.sms, !subscribedSms));

            trackSmsNotificationToggle({
                auctionRemindersEnabled:
                    alertTypeId === AlertTypes.AuctionReminder
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.AuctionReminder]?.subscribed ?? false),
                eventTrigger: TrackSmsNotificationToggleEventTrigger.EmailsAndNotifications,
                outbidEnabled:
                    alertTypeId === AlertTypes.Outbid
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.Outbid]?.subscribed ?? false),
                paymentRemindersEnabled:
                    alertTypeId === AlertTypes.PaymentReminder
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.PaymentReminder]?.subscribed ?? false),
            });
        } else {
            dispatch(openSMSConfirmationModal(alertTypeId));
        }
    };

    const smsSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.sms);
    const subscribedSms = Boolean(smsSubscription?.subscribed);

    const browserSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.browser);
    const subscribedBrowser = Boolean(browserSubscription?.subscribed);

    const emailSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.email);
    const subscribedEmail = Boolean(emailSubscription?.subscribed);

    const permission = useAppSelector(getPermission);
    const permissionGranted = permission === NotificationPermissions.Granted;
    const permissionDenied = permission === NotificationPermissions.Denied;

    const { width } = useWindowSize();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();

    const toggleEmailAlerts = React.useCallback(
        () => dispatch(toggleSmsAlert(alertTypeId, DeliveryType.email, !subscribedEmail)),
        [alertTypeId, dispatch, subscribedEmail]
    );

    const browserPermission = React.useCallback(() => dispatch(requestBrowserPermission()), [dispatch]);

    const toggleBroswerSms = React.useCallback(
        () => dispatch(toggleSmsAlert(alertTypeId, DeliveryType.browser, !subscribedBrowser)),
        [alertTypeId, dispatch, subscribedBrowser]
    );

    const handleBrowserSubscriptionClick = () => {
        if (!permissionGranted && !subscribedBrowser) {
            browserPermission();
        } else {
            toggleBroswerSms();
        }
    };

    return (
        <TableRow
            browser={
                browserSubscription && !permissionDenied ? (
                    <StyledCheckbox
                        checked={subscribedBrowser}
                        label={formatMessage(
                            {
                                id: 'notificationsPage.toggleBrowserNotifcations',
                            },
                            { name: subscriptions[0]?.name }
                        )}
                        labelDisplay=""
                        name="email-alert-checkbox"
                        onClick={handleBrowserSubscriptionClick}
                    />
                ) : browserSubscription ? (
                    <>
                        <div id={`browser-notif-disabled-${alertTypeId}`}>
                            <StyledCheckbox
                                checked={subscribedBrowser}
                                disabled
                                label={formatMessage(
                                    {
                                        id: 'notificationsPage.toggleBrowserNotifcations',
                                    },
                                    { name: subscriptions[0]?.name }
                                )}
                                labelDisplay=""
                                name="browserSubscription"
                                onClick={noop}
                            />
                        </div>
                        <Tooltip
                            anchorSelect={`#browser-notif-disabled-${alertTypeId}`}
                            className="max-w-[250px]"
                            place="top"
                        >
                            <PopoverContainer>
                                <FormattedMessage id="notificationsPage.disabled" />
                                <div>
                                    <Link
                                        href={enableBrowserNotificationsUrl}
                                        target="_blank"
                                    >
                                        <FormattedMessage id="notifications.enable_browser_notifications" />
                                    </Link>
                                </div>
                            </PopoverContainer>
                        </Tooltip>
                    </>
                ) : null
            }
            className={className}
            email={
                emailSubscription && (
                    <StyledCheckbox
                        checked={subscribedEmail}
                        label={formatMessage(
                            { id: 'notificationsPage.toggleEmailNotifcations' },
                            { name: subscriptions[0]?.name }
                        )}
                        labelDisplay=""
                        name="email-alert-checkbox"
                        onClick={toggleEmailAlerts}
                    />
                )
            }
            frequency={
                <BodySecondary>
                    <FormattedMessage id={`notificationsPage.table.frequency.${alertTypeId}`} />
                </BodySecondary>
            }
            sms={
                smsAllowed &&
                smsSubscription && (
                    <StyledCheckbox
                        checked={subscribedSms}
                        label={formatMessage(
                            { id: 'notificationsPage.toggleSMSNotifcations' },
                            { name: subscriptions[0]?.name }
                        )}
                        labelDisplay=""
                        name="email-alert-checkbox"
                        onClick={smsBoxCheck}
                    />
                )
            }
            type={
                <>
                    <Text
                        h5={width > tabletNarrowWidthNum}
                        h6={width <= tabletNarrowWidthNum}
                    >
                        {subscriptions[0]?.name}
                    </Text>
                    <Text
                        caption={width <= tabletNarrowWidthNum}
                        colorPrimaryWeak
                        secondary={width > tabletNarrowWidthNum}
                    >
                        <FormattedMessage id={`notificationsPage.table.descriptions.${alertTypeId}`} />
                    </Text>
                </>
            }
        />
    );
};

export default React.memo<Props>(AlertsRow);

const PopoverContainer = styled.div`
    padding: 15px;
    max-width: 250px;
`;

const StyledCheckbox = styled(Checkbox)`
    margin-bottom: 0;
`;
